// src/components/Overlays/ModalShare.jsx
//
// Share modal — lets project owners/editors create and manage a public share link.
//
// Props:
//   isOpen       {boolean}
//   onClose      {function}
//   linkType     {'project'|'doc'}
//   targetId     {number}   project_id or doc_id
//   projectId    {number}   always the project_id (for visual_state context)
//   visualState  {object|null}  current view state: { openTabs, activePane, splitScreen }
//   user         {object}   current authed user
//
// Behaviour:
//   - On open: fetches existing share links for the target
//   - Shows the share URL with a copy button
//   - Toggle to enable/disable the link
//   - Expiry date picker (optional, null = never)
//   - "Capture current view" button to snapshot visual_state
//   - "Revoke" button to permanently delete the link

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from './Modal';
import AppInput from '../Inputs/AppInput';
import {
  apiCreateShareLink,
  apiGetShareLinksForTarget,
  apiToggleShareLink,
  apiRevokeShareLink,
  apiSetShareLinkExpiry,
  apiUpdateShareLinkVisualState,
  buildShareUrl,
} from '../../api/shareLinks';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatExpiry(iso) {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function isExpired(iso) {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

function isActive(link) {
  if (!link) return false;
  if (!link.enabled) return false;
  if (isExpired(link.expires_at)) return false;
  return true;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ModalShare({
  isOpen,
  onClose,
  linkType = 'project',
  targetId,
  projectId,
  visualState = null,
  user,
}) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Expiry picker state
  const [showExpiryPicker, setShowExpiryPicker] = useState(false);
  const [expiryValue, setExpiryValue] = useState(''); // ISO date string (date-only)

  const copyTimeoutRef = useRef(null);

  // The first enabled (active) link is the "primary" one we display
  const primaryLink = links.find(l => l.enabled) || links[0] || null;

  const load = useCallback(async () => {
    if (!targetId) return;
    setLoading(true);
    setError(null);
    try {
      const fetched = await apiGetShareLinksForTarget(linkType, targetId);
      setLinks(fetched);
    } catch (err) {
      setError('Failed to load share links.');
    } finally {
      setLoading(false);
    }
  }, [linkType, targetId]);

  useEffect(() => {
    if (isOpen) {
      load();
      setShowExpiryPicker(false);
      setExpiryValue('');
      setCopied(false);
    }
  }, [isOpen, load]);

  useEffect(() => () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const result = await apiCreateShareLink(linkType, targetId, user?.id, visualState ?? null);
      setLinks(prev => [{ ...result.link, shareUrl: result.shareUrl, isActive: true }, ...prev]);
    } catch (err) {
      setError(err.message || 'Failed to create share link.');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (link) => {
    setError(null);
    try {
      const updated = await apiToggleShareLink(link.id, !link.enabled);
      setLinks(prev => prev.map(l => (l.id === link.id ? updated : l)));
    } catch (err) {
      setError(err.message || 'Failed to update share link.');
    }
  };

  const handleRevoke = async (link) => {
    if (!window.confirm('Permanently delete this share link? Anyone with the URL will lose access.')) return;
    setError(null);
    try {
      await apiRevokeShareLink(link.id);
      setLinks(prev => prev.filter(l => l.id !== link.id));
    } catch (err) {
      setError(err.message || 'Failed to revoke share link.');
    }
  };

  const handleSetExpiry = async (link) => {
    setError(null);
    try {
      const isoValue = expiryValue ? new Date(expiryValue + 'T23:59:59').toISOString() : null;
      await apiSetShareLinkExpiry(link.id, isoValue);
      setLinks(prev => prev.map(l => (l.id === link.id ? { ...l, expires_at: isoValue } : l)));
      setShowExpiryPicker(false);
      setExpiryValue('');
    } catch (err) {
      setError(err.message || 'Failed to update expiry.');
    }
  };

  const handleCaptureView = async (link) => {
    if (!visualState) return;
    setError(null);
    try {
      await apiUpdateShareLinkVisualState(link.id, visualState);
      setLinks(prev => prev.map(l => (l.id === link.id ? { ...l, visual_state: visualState } : l)));
    } catch (err) {
      setError(err.message || 'Failed to capture view state.');
    }
  };

  const handleCopy = (url) => {
    if (!url) return;
    try {
      navigator.clipboard.writeText(url).catch(() => {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      });
    } catch {
      //
    }
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const shareUrl = primaryLink ? buildShareUrl(primaryLink) : null;
  const linkActive = isActive(primaryLink);
  const kindLabel = linkType === 'project' ? 'project' : 'document';

  return (
    <Modal id="modalShare" isOpen={isOpen} onCancel={onClose} onConfirm={onClose} title={`Share ${kindLabel}`} confirmText="Done" cancelText="Close">
      <div className="modalShare">
        {loading && (
              <p className="shareModal__hint modalDescriptor">Loading…</p>
            )}

        {!loading && links.length === 0 && (
          <div className="shareModal__empty modalBlock">
            <p className="shareModal__hint modalDescriptor">
              Create a public link to share this {kindLabel} in read-only view.
              Anyone with the link can view it — no account required.
            </p>
            <button
              type="button"
              className="button button--primary"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? 'Creating…' : 'Create share link'}
            </button>
          </div>
        )}

        {!loading && primaryLink && (
          <div className="shareModal__body modalSubsectionBody">
            {/* Status badge */}
            <div className="shareModal__statusRow modalRow">
              <span className={`shareModal__badge shareModal__badge--${linkActive ? 'active' : 'inactive'}`}>
                {isExpired(primaryLink.expires_at)
                  ? 'Expired'
                  : primaryLink.enabled ? 'Active' : 'Disabled'}
              </span>
              {primaryLink.expires_at && !isExpired(primaryLink.expires_at) && (
                <span className="shareModal__hint modalDescriptor" style={{ marginLeft: 8 }}>
                  Expires {formatExpiry(primaryLink.expires_at)}
                </span>
              )}
            </div>

            {/* URL row */}
            <div className="shareModal__urlRow modalRow">
              <AppInput
                className="input"
                type="text"
                readOnly
                value={shareUrl || ''}
                onClick={e => e.target.select()}
                aria-label="Share URL"
              />
              <div className="modalRowControl">
                <button
                  type="button"
                  className={`button button--secondary ${copied ? 'shareModal__copied' : ''}`}
                  onClick={() => handleCopy(shareUrl)}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            {/* Enable / disable toggle */}
            <div className="shareModal__row modalRow">
              <label className="shareModal__label modalRowLabel">
                <input
                  type="checkbox"
                  checked={!!primaryLink.enabled}
                  onChange={() => handleToggle(primaryLink)}
                  aria-label="Enable share link"
                />
                {' '}
                Link enabled
              </label>
            </div>

            {/* Expiry */}
            <div className="shareModal__row modalRow">
              <span className="shareModal__label modalRowLabel">
                Expires: {formatExpiry(primaryLink.expires_at)}
              </span>
              <div className="modalRowControl">
                <button
                  type="button"
                  className="button button--ghost button--sm"
                  onClick={() => setShowExpiryPicker(prev => !prev)}
                >
                  {showExpiryPicker ? 'Cancel' : 'Change'}
                </button>
                {primaryLink.expires_at && !showExpiryPicker && (
                  <button
                    type="button"
                    className="button button--ghost button--sm"
                    onClick={async () => {
                      await apiSetShareLinkExpiry(primaryLink.id, null);
                      setLinks(prev => prev.map(l => (l.id === primaryLink.id ? { ...l, expires_at: null } : l)));
                    }}
                    style={{ marginLeft: 4 }}
                  >
                    Remove expiry
                  </button>
                )}
              </div>
            </div>
            {showExpiryPicker && (
              <div className="shareModal__row shareModal__row--indent modalRow">
                <AppInput
                  type="date"
                  className="input"
                  value={expiryValue}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setExpiryValue(e.target.value)}
                  aria-label="Expiry date"
                />
                <div className="modalRowControl">
                  <button
                    type="button"
                    className="button button--primary button--sm"
                    disabled={!expiryValue}
                    onClick={() => handleSetExpiry(primaryLink)}
                    style={{ marginLeft: 8 }}
                  >
                    Set
                  </button>
                </div>
              </div>
            )}

            {/* Capture current view */}
            {linkType === 'project' && visualState && (
              <div className="shareModal__row modalRow">
                <div className="modalRowControl">
                  <button
                    type="button"
                    className="button button--secondary"
                    onClick={() => handleCaptureView(primaryLink)}
                  >
                    Capture current view
                  </button>
                </div>
                <span className="shareModal__hint modalDescriptor" style={{ marginLeft: 8 }}>
                  Save open tabs &amp; layout to the link
                </span>
              </div>
            )}

            {/* Danger zone */}
            <div className="shareModal__divider modalSeparator" />
            <div className="shareModal__row shareModal__row--danger modalRow">
              <div className="modalRowControl">
                <button
                  type="button"
                  className="button button--danger button--sm"
                  onClick={() => handleRevoke(primaryLink)}
                >
                  Revoke link
                </button>
              </div>
              <span className="shareModal__hint modalDescriptor" style={{ marginLeft: 8 }}>
                Permanently delete this link — all existing URLs will stop working
              </span>
            </div>
          </div>
        )}

        {error && (
          <p className="shareModal__error" role="alert">{error}</p>
        )}
      </div>
    </Modal>
  );
}
